import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, signal } from "@angular/core";
import { RouterLink } from "@angular/router";
import { TranslatePipe } from "@ngx-translate/core";
import { finalize } from "rxjs";

import { AdaaHelper } from "../../../../core/utils";
import { FormInputFileComponent, WfOverrideDecisionComponent } from "../../../../shared/components";
import { FileInputType } from "../../../../shared/constants/enums";
import { AttachFile, WorkflowDecisionModelType } from "../../../../shared/models";
import { NotificationCommunication } from "../../../../shared/models/notification-center-model";
import { Adaa4NotificationsService } from "../../services/adaa4-notifications-service";
import { Adaa4StepperComponent } from "../adaa4-stepper/adaa4-stepper";

@Component({
  selector: "adaa-adaa4-notification-action",
  imports: [TranslatePipe, RouterLink, FormInputFileComponent, WfOverrideDecisionComponent, Adaa4StepperComponent],
  templateUrl: "./adaa4-notification-action.html",
  styleUrl: "./adaa4-notification-action.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Adaa4NotificationActionComponent implements OnInit {
  private readonly _notificationsService = inject(Adaa4NotificationsService);

  readonly AdaaHelper = AdaaHelper;
  readonly allowedFiles: FileInputType[] = [
    FileInputType.txt,
    FileInputType.pdf,
    FileInputType.doc,
    FileInputType.docx,
    FileInputType.xls,
    FileInputType.xlsx,
    FileInputType.png,
    FileInputType.jpeg,
    FileInputType.jpg,
  ];

  id = input.required<string>();

  notification = signal<NotificationCommunication | undefined>(undefined);
  loaded = signal(false);
  decision = signal<Partial<WorkflowDecisionModelType> | undefined>(undefined);
  attachment = signal<AttachFile | null>(null);
  submitting = signal(false);
  result = signal<"approved" | "rejected" | undefined>(undefined);

  actionType = computed(() => {
    const notification = this.notification();
    return notification ? this._notificationsService.actionType(notification) : "other";
  });
  isPending = computed(() => {
    const notification = this.notification();
    return !!notification && this._notificationsService.isPending(notification);
  });
  addedComments = computed(() => this.decision()?.categorizedComments ?? []);
  canReject = computed(() => this.addedComments().length > 0);

  public ngOnInit(): void {
    this._notificationsService.open(+this.id()).subscribe((notification) => {
      this.notification.set(notification);
      this.loaded.set(true);
    });
  }

  public submit(approval: boolean): void {
    const notification = this.notification();
    if (!notification || this.submitting()) return;

    this.submitting.set(true);
    this._notificationsService
      .decide({ notification, approval, attachment: this.attachment(), decision: this.decision() })
      .pipe(finalize(() => this.submitting.set(false)))
      .subscribe(() => this.result.set(approval ? "approved" : "rejected"));
  }
}
